import { useEffect, useState, useCallback } from "react";
import { Search, Filter, ChevronLeft, ChevronRight, Eye, X, Mail, Download } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { formatPrice, formatAddress } from "../../lib/format";
import { toast } from "sonner";

const STATUSES = ["all", "confirmed", "paid", "processing", "packed", "shipped", "out_for_delivery", "delivered", "cancelled", "returned", "refunded"];

const STATUS_LABELS = {
  confirmed: "Confirmed", paid: "Paid", processing: "Processing", packed: "Packed",
  shipped: "Shipped", out_for_delivery: "Out for Delivery", delivered: "Delivered",
  cancelled: "Cancelled", returned: "Returned", refunded: "Refunded",
};

export default function AdminOrders({ openOrderId, onClose }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(openOrderId || null);

  useEffect(() => { setSelectedId(openOrderId || null); }, [openOrderId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders", {
        params: { q: q || undefined, status, date_from: dateFrom || undefined, date_to: dateTo || undefined, page, page_size: 25 },
      });
      setData(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  }, [q, status, dateFrom, dateTo, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Orders</p>
          <h2 className="font-serif text-4xl">{data.total} {data.total === 1 ? "order" : "orders"}</h2>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#141414] p-5 grid md:grid-cols-4 gap-4">
        <div className="relative md:col-span-2">
          <Search size={14} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-gold pointer-events-none" />
          <input
            placeholder="Search by Order ID, customer name, email, or phone"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            className="input-line pl-7 w-full"
            data-testid="orders-search"
          />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="input-line bg-transparent" data-testid="orders-status-filter">
          {STATUSES.map(s => <option key={s} value={s} className="bg-[#0C0C0C]">{s === "all" ? "All statuses" : STATUS_LABELS[s] || s}</option>)}
        </select>
        <div className="flex gap-2">
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="input-line" data-testid="orders-date-from" />
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="input-line" data-testid="orders-date-to" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#141414] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-[rgba(244,244,240,0.06)]">
              <Th>Order</Th><Th>Customer</Th><Th>Phone</Th><Th>Items</Th><Th>Total</Th><Th>Status</Th><Th>Date</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-muted">No orders match these filters.</td></tr>
            ) : data.items.map(o => (
              <tr key={o.order_id} className="border-b border-[rgba(244,244,240,0.04)] hover:bg-[#1a1a1a]" data-testid={`order-row-${o.order_id}`}>
                <Td><span className="font-mono text-[11px]">{o.order_id}</span></Td>
                <Td>{o.shipping?.name || o.user_email}</Td>
                <Td className="text-muted">{o.shipping?.phone || "-"}</Td>
                <Td className="text-muted">{(o.items?.length) || 0}</Td>
                <Td className="text-gold">{formatPrice(o.total)}</Td>
                <Td><StatusPill status={o.status} /></Td>
                <Td className="text-muted text-xs">{new Date(o.created_at).toLocaleString()}</Td>
                <Td><button onClick={() => setSelectedId(o.order_id)} className="text-muted hover:text-gold p-1" data-testid={`open-order-${o.order_id}`}><Eye size={14} strokeWidth={1.5} /></button></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-muted">
        <span>Page {data.page} of {data.pages}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="btn-outline !px-3 !py-2 disabled:opacity-30"><ChevronLeft size={14} /></button>
          <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page >= data.pages} className="btn-outline !px-3 !py-2 disabled:opacity-30"><ChevronRight size={14} /></button>
        </div>
      </div>

      {selectedId && <OrderDetailDrawer orderId={selectedId} onClose={() => { setSelectedId(null); onClose?.(); load(); }} />}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-muted font-normal">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }

function StatusPill({ status }) {
  const map = {
    confirmed: "border-gold text-gold", paid: "border-gold text-gold", processing: "border-blue-400 text-blue-400",
    packed: "border-cyan-400 text-cyan-400", shipped: "border-cyan-400 text-cyan-400",
    out_for_delivery: "border-emerald-300 text-emerald-300", delivered: "border-emerald-400 text-emerald-400",
    cancelled: "border-red-400 text-red-400", returned: "border-amber-400 text-amber-400", refunded: "border-amber-400 text-amber-400",
  };
  return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] border ${map[status] || "border-muted text-muted"}`}>{STATUS_LABELS[status] || status}</span>;
}

function OrderDetailDrawer({ orderId, onClose }) {
  const [order, setOrder] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/orders/${orderId}`);
    setOrder(data);
    setNewStatus(data.status || "");
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async () => {
    if (newStatus === order.status && !note) { toast.message("No change"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}/status`, { status: newStatus, note });
      setOrder(data);
      setNote("");
      toast.success(`Status updated → ${STATUS_LABELS[newStatus] || newStatus}`);
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  const resendEmail = async () => {
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/resend-email`);
      if (data.ok) {
        toast.success(`Confirmation email re-sent to ${data.to || order.user_email}`, { duration: 5000 });
      } else if (data.skipped) {
        toast.message(`Email skipped — ${data.reason || "Resend not configured"}`, { duration: 5000 });
      } else {
        const raw = data.error || "Unknown error";
        // Detect Resend sandbox restriction and show a friendlier message
        const friendly = /testing emails to your own email/i.test(raw)
          ? "Resend is in sandbox mode — verify your domain at resend.com/domains, then set Sender Email in Admin → Settings → Email (Resend)."
          : raw;
        toast.error(`Email send failed: ${friendly}`, { duration: 9000 });
      }
    } catch (e) { toast.error(formatErr(e), { duration: 6000 }); }
  };

  const downloadPdf = async () => {
    try {
      const { data } = await api.get(`/admin/orders/${orderId}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `GOSHI-invoice-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const downloadGstPdf = async () => {
    try {
      const { data, headers } = await api.get(`/admin/orders/${orderId}/gst-invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      // Server sets an INV-####/YY-YY filename via Content-Disposition; fall back if missing
      const cd = headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)/.exec(cd);
      const filename = m ? m[1] : `GST-invoice-${orderId}.pdf`;
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("GST invoice downloaded");
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!order) return null;

  return (
    <div className="fixed inset-0 z-[80] flex">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative ml-auto bg-[#0C0C0C] border-l border-[rgba(244,244,240,0.08)] w-full max-w-2xl h-full overflow-y-auto p-8 space-y-6" data-testid="order-detail-drawer">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-2">Order</p>
            <h2 className="font-serif text-3xl">{order.order_id}</h2>
            <p className="text-xs text-muted mt-1">{new Date(order.created_at).toLocaleString()}</p>
            <EmailBadge email={order.email} adminEmail={order.admin_email} />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadPdf} className="btn-outline !px-3 !py-2 text-[10px] flex items-center gap-1.5" data-testid="download-pdf-btn" title="Download PDF invoice">
              <Download size={11} strokeWidth={1.5} /> PDF
            </button>
            <button onClick={downloadGstPdf} className="btn-outline !px-3 !py-2 text-[10px] flex items-center gap-1.5" data-testid="download-gst-pdf-btn" title="Download Indian GST tax invoice">
              <Download size={11} strokeWidth={1.5} /> GST
            </button>
            <button onClick={resendEmail} className="btn-outline !px-3 !py-2 text-[10px] flex items-center gap-1.5" data-testid="resend-email-btn" title="Re-send confirmation email">
              <Mail size={11} strokeWidth={1.5} /> Re-send email
            </button>
            <button onClick={onClose} className="text-muted hover:text-gold"><X size={20} strokeWidth={1.5} /></button>
          </div>
        </div>

        {/* Customer + address */}
        <div className="grid sm:grid-cols-2 gap-5 bg-[#141414] p-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold mb-2">Customer</p>
            <p className="text-sm">{order.shipping?.name}</p>
            <p className="text-xs text-muted">{order.user_email}</p>
            <p className="text-xs text-muted">{order.shipping?.phone}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold mb-2">Delivery</p>
            <p className="text-xs leading-relaxed">
              {formatAddress(order.shipping || {})}
            </p>
          </div>
        </div>

        {/* Items */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-gold mb-3">Items</p>
          <ul className="space-y-3">
            {(order.items || []).map((i, idx) => (
              <li key={`${i.product_id || idx}-${i.size || idx}`} className="flex gap-3 bg-[#141414] p-3">
                <img src={i.image} alt="" className="w-12 h-16 object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-sm truncate">{i.name}</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted mt-1">Size {i.size} · Qty {i.quantity}</p>
                </div>
                <p className="text-sm text-gold">{formatPrice(i.price * i.quantity)}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* Payment + Shiprocket */}
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="bg-[#141414] p-5 text-xs space-y-1.5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold mb-2">Payment</p>
            <p>Method · <span className="text-text capitalize">{order.payment?.method || "Razorpay"}</span></p>
            {order.payment?.vpa && <p>VPA · <span className="text-text">{order.payment.vpa}</span></p>}
            <p>Payment ID · <span className="text-text font-mono text-[11px]">{order.payment?.razorpay_payment_id}</span></p>
            <p className="pt-2 border-t border-[rgba(244,244,240,0.06)]">Subtotal · {formatPrice(order.subtotal || 0)}</p>
            {order.discount > 0 && <p className="text-gold">Discount · -{formatPrice(order.discount)}</p>}
            <p className="text-gold font-serif text-lg pt-1">Total · {formatPrice(order.total)}</p>
          </div>
          <div className="bg-[#141414] p-5 text-xs space-y-1.5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold mb-2">Shipment</p>
            {order.shiprocket?.ok ? (
              <>
                <p>Shiprocket Order · <span className="text-text">{order.shiprocket.shiprocket_order_id}</span></p>
                <p>Shipment ID · <span className="text-text">{order.shiprocket.shipment_id}</span></p>
                {order.shiprocket.awb_code && <p>AWB · <span className="text-gold">{order.shiprocket.awb_code}</span></p>}
              </>
            ) : order.shiprocket?.skipped ? (
              <p className="text-muted/70">Not configured</p>
            ) : (
              <p className="text-red-400">Push failed{order.shiprocket?.error ? `: ${order.shiprocket.error}` : ""}</p>
            )}
          </div>
        </div>

        {/* Status updater */}
        <div className="bg-[#141414] p-5 space-y-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gold">Update Status</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="input-line bg-transparent flex-1" data-testid="status-select">
              {STATUSES.filter(s => s !== "all").map(s => <option key={s} value={s} className="bg-[#0C0C0C]">{STATUS_LABELS[s]}</option>)}
            </select>
            <input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="input-line flex-1" data-testid="status-note" />
            <button onClick={updateStatus} disabled={saving} className="btn-gold" data-testid="save-status-btn">{saving ? "Saving…" : "Update"}</button>
          </div>
        </div>

        {/* Audit timeline */}
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-gold mb-3">Activity Timeline</p>
          <ol className="space-y-3 border-l border-[rgba(200,169,126,0.3)] pl-5">
            <li className="relative">
              <span className="absolute -left-[26px] top-1 w-2 h-2 rounded-full bg-gold" />
              <p className="text-sm text-text">Order placed</p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted">{new Date(order.created_at).toLocaleString()}</p>
            </li>
            {(order.audit_log || []).map((a, i) => (
              <li key={`${a.at || i}-${a.to || ""}`} className="relative">
                <span className="absolute -left-[26px] top-1 w-2 h-2 rounded-full bg-gold" />
                <p className="text-sm text-text">Status: <span className="text-gold">{STATUS_LABELS[a.to] || a.to}</span></p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted">{new Date(a.at).toLocaleString()} · by {a.by_email}</p>
                {a.note && <p className="text-xs text-muted/80 mt-1 italic">"{a.note}"</p>}
              </li>
            ))}
          </ol>
        </div>
      </aside>
    </div>
  );
}

function pill(state) {
  // returns { label, tone }
  if (!state) return { label: "Not sent", tone: "text-muted/60 border-[rgba(244,244,240,0.1)]" };
  if (state.skipped) return { label: "Skipped", tone: "text-amber-400 border-amber-400/30" };
  if (state.ok) return { label: "Sent", tone: "text-emerald-400 border-emerald-400/30" };
  return { label: "Failed", tone: "text-red-400 border-red-400/30" };
}

function EmailBadge({ email, adminEmail }) {
  const cust = pill(email);
  const admin = pill(adminEmail);
  return (
    <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="email-status-badges">
      <span className={`text-[9px] uppercase tracking-[0.2em] border px-2 py-0.5 ${cust.tone}`} title={email?.error || email?.reason || (email?.email_id ? `ID: ${email.email_id}` : "")} data-testid="customer-email-pill">
        Customer · {cust.label}
      </span>
      <span className={`text-[9px] uppercase tracking-[0.2em] border px-2 py-0.5 ${admin.tone}`} title={adminEmail?.error || adminEmail?.reason || (adminEmail?.email_id ? `ID: ${adminEmail.email_id}` : "")} data-testid="admin-email-pill">
        Admin · {admin.label}
      </span>
    </div>
  );
}

